import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { ProjectsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as ProjectActions from "../../../actions/project";
import { FormFilterInput } from "../../../components/form";
import {
    RestrictedLayout,
    TruncatedCell
} from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "../../../helpers/date";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./ProjectsPage.scss";

interface ProjectsPageProps {
    formFilter: IFormFilter;
    projects: Dtos.Project[];
    loadingProjects: boolean;
    loadProjectsSuccess: boolean;
    loadProjectsFailure: boolean;
    loadProjectsErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearProjects: ProjectActions.IProjectClearActionCreator;
    loadProjects: ProjectActions.IProjectLoadActionCreator;
    filter: ProjectActions.IProjectFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const ProjectsPage: FC<ProjectsPageProps> = ({
    formFilter,
    projects,
    loadingProjects,
    loadProjectsSuccess,
    loadProjectsFailure,
    loadProjectsErrorCode,
    permissions,
    pageSize,
    clearProjects,
    loadProjects,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showProjects = !loadingProjects && loadProjectsSuccess;


    // Load projects
    useEffect(() => {
        if (!initalised) {
            setDrawerItems([]);
            setBreadcrumbs(ProjectsBreadcrumbs());

            loadProjects();

            setInitalised(true);
        }
    }, [
        loadProjects,
        setDrawerItems,
        setBreadcrumbs,
        ProjectsBreadcrumbs,
        initalised,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProjectsFailure) {
            navigate(`/error/${loadProjectsErrorCode}`);
        }
    }, [loadProjectsFailure, loadProjectsErrorCode, navigate]);

    // Cleanup effect to clear the projects on component unmount
    useEffect(() => {
        return () => {
            clearProjects();
        };
    }, [clearProjects]);

    const createTable = useMemo(() => {
        if (!projects || projects.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no projects to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = projects.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={projects}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : projects.length
                    }
                    pageSize={showPagination ? pageSize : projects.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no projects to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const projectId = rowInfo.original.id;
                                const _canView =
                                    AuthenticationHelper.hasPermission(
                                        permissions,
                                        Dtos.Permission.ProjectView,
                                        projectId,
                                        undefined
                                    );

                                let url: string = "";

                                if (rowInfo.original.projectOutcomeId === 1) {
                                    url = `/projects/${rowInfo.original.id}/details`;
                                } else {
                                    url = `/trials/${rowInfo.original.id}/project/details`;
                                }

                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "projectDateSubmitted",
                            desc: true,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (props) =>
                                props?.original?.projectOutcomeId === 2 ? (
                                    <FontAwesomeIcon
                                        size="lg"
                                        icon={FontAwesomeIcons.Duotone.TRIAL}
                                        fixedWidth
                                    />
                                ) : (
                                    <FontAwesomeIcon
                                        size="lg"
                                        icon={
                                            FontAwesomeIcons.Duotone.CLIPBOARD
                                        }
                                        fixedWidth
                                    />
                                ),
                        },
                        {
                            id: "projectDateSubmitted",
                            Header: "Submitted to BCT",
                            accessor: "projectDateSubmitted",
                            maxWidth: 180,
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "projectShortTitle",
                            Header: "Short Title",
                            accessor: "projectShortTitle",
                            maxWidth: 280,
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "projectScientificTitle",
                            Header: "Scientific Title",
                            accessor: "projectScientificTitle",
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [projects, pageSize, PAGE_SIZE_OPTIONS]);

    const renderContent = useMemo(() => {
        const canCreate = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.ProjectCreate
        );

        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Projects</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {canCreate && (
                            <>
                                <Link
                                    className={"btn btn-primary"}
                                    url={"/projects/create"}
                                >
                                    Create
                                </Link>
                            </>
                        )}
                    </div>
                </div>
                {!showProjects ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showProjects ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [permissions, formFilter, filter, showProjects, createTable, projects]);

    return (
        <RestrictedLayout
            anyMatchpermission={Dtos.Permission.ProjectView}
            loading={loadingProjects}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<ProjectsPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        projects: state.projects.formFilteredData!,
        formFilter: state.projects.formFilter,
        loadingProjects:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Pending,
        loadProjectsSuccess:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Success,
        loadProjectsFailure:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Failure,
        loadProjectsErrorCode: state.projects.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<ProjectsPageProps> => {
    return {
        loadProjects: bindActionCreators(ProjectActions.LoadProject, dispatch),
        clearProjects: bindActionCreators(ProjectActions.Clear, dispatch),
        filter: bindActionCreators(ProjectActions.Filter, dispatch),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectsPage);
